import React from 'react';
import styled from 'styled-components';
import { system, extractSysProps } from './system';

// Replicates molekule Row:
//   flex: 0 1 auto
//   flex-direction: [vertical ? column : row][reverse ? -reverse : '']
//   flex-wrap: wrap
//   margin-right/left: -gutter/2 (or 0 if collapse)
//
// Props: vertical, reverse, collapse, gutter, plus all system props

export type RowProps = {
  vertical?: boolean;
  reverse?: boolean;
  collapse?: boolean;
  gutter?: number;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
};

interface StyledRowInternalProps {
  $vertical?: boolean;
  $reverse?: boolean;
  $collapse?: boolean;
  $gutter?: number;
  $sys: Record<string, any>;
  [key: string]: any;
}

function getRowMargin(p: any): number {
  if (p.$collapse) return 0;
  const g =
    p.$gutter !== undefined ? p.$gutter : (p.theme && p.theme.gridGutter) || 16;
  return g / 2;
}

function getFlexDirection(p: any): string {
  const base = p.$vertical ? 'column' : 'row';
  return p.$reverse ? `${base}-reverse` : base;
}

const StyledRow = styled.div<StyledRowInternalProps>`
  flex: 0 1 auto;
  flex-direction: ${getFlexDirection};
  flex-wrap: wrap;
  display: flex;
  margin-right: ${(p: any) => `-${getRowMargin(p)}px`};
  margin-left: ${(p: any) => `-${getRowMargin(p)}px`};
  ${(p: any) => system(p.$sys, p.theme)}
`;

const Row: React.FC<RowProps> = ({
  vertical = false,
  reverse = false,
  collapse = false,
  gutter,
  children,
  ...props
}) => {
  const { sys, rest } = extractSysProps(props);

  // Pass gutter/collapse down to Column children only (Column reads them).
  // Injecting these into arbitrary elements would forward invalid DOM props.
  const clonedChildren = React.Children.map(children, (child) => {
    if (
      React.isValidElement(child) &&
      (child.type as any)?.displayName === 'Column'
    ) {
      return React.cloneElement(child as any, { gutter, collapse, vertical });
    }
    return child;
  });

  return (
    <StyledRow
      $vertical={vertical}
      $reverse={reverse}
      $collapse={collapse}
      $gutter={gutter}
      $sys={sys}
      {...rest}
    >
      {clonedChildren}
    </StyledRow>
  );
};

Row.displayName = 'Row';

export default Row;
